import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Row, Col, ListGroup, Image, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { useGetOrderDetailsQuery, usePayOrderMutation, useGetPayPalClientIdQuery, useDeliverOrderMutation } from '../slices/ordersApiSlice';
import { formatNTD } from '../utils/pricing';
import LinePayButton from '../components/LinePayButton';
import { DELIVERY_TIME_SLOTS } from '../utils/shippingContact';


const OrderScreen = () => {
  const { id: orderId } = useParams();

  const { data: order, refetch, isLoading, error, } = useGetOrderDetailsQuery(orderId);

  const [ payOrder, { isLoading: loadingPay }] = usePayOrderMutation();

  const [ deliverOrder, { isLoading: loadingDeliver } ] = useDeliverOrderMutation();

  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();

  const { 
    data: paypal, 
    isLoading: loadingPayPal, 
    error: errorPayPal, 
  } = useGetPayPalClientIdQuery();

  const { userInfo } = useSelector((state) => state.auth);

  // LINE Pay orders pay through LinePayButton, so they skip the PayPal SDK.
  const isLinePay = order?.paymentMethod === 'LinePay';

  // LINE Pay cancelUrl = /order/:id?linepay=cancel
  const [searchParams] = useSearchParams();
  const linePayCancelled = searchParams.get('linepay') === 'cancel';
  useEffect(() => {
    if (linePayCancelled) toast.info('已取消 LINE Pay 付款，可以再試一次');
  }, [linePayCancelled]);
  
  useEffect(() => {
    if (isLinePay) return; // no PayPal script needed for LINE Pay orders
    if (!errorPayPal && !loadingPayPal && paypal.clientId) {
      const loadPayPalScript = async () => {
        paypalDispatch({
          type: "resetOptions",
          value: {
            "client-id": paypal.clientId,
            // Prices are NTD. PayPal TWD accepts whole amounts only.
            currency: "TWD",
          },
        });
        paypalDispatch({ type: "setLoadingStatus", value: "pending" });
      };
      if (order && !order.isPaid) {
        if (!window.paypal) {
          loadPayPalScript();
        }
      }
    }
  }, [order, paypal, paypalDispatch, loadingPayPal, errorPayPal, isLinePay]);

  function onApprove(data, actions) {
    return actions.order.capture().then(async function (details) {
      try {
        await payOrder({ orderId, details }).unwrap();
        refetch();
        toast.success("付款成功");
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    });
  }

  // async function onApproveTest() {
  //   await payOrder({ orderId, details: { payer: {} } });
  //   refetch();
  //   toast.success("Payment successful");
  // }

  function onError(err) {
    toast.error(err.message);
  }

  function createOrder(data, actions) {
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: String(Math.round(order.totalPrice)),
            currency_code: "TWD",
          },
        },
      ],
    }).then((orderId) => { 
      return orderId;
    } )
  }

  const deliverOrderHandler = async () => {
    try {
      await deliverOrder(orderId);
      refetch();
      toast.success("訂單已標記為出貨");
    } catch (err) {
      toast.error(err?.data?.message || err.message )
    }
  }
  return isLoading ? (<Loader />) : error ? (<Message variant="danger">{error?.data?.message || error.error }</Message>) : (
    <>
      <h1>訂單 {order._id}</h1>
      <Row>
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>運送資訊</h2>
              <p>
                <strong>姓名：</strong> {order.user.name}
              </p>
              <p>
                <strong>電子郵件：</strong> {order.user.email}
              </p>
              {order.shippingMethod?.name && (
                <p>
                  <strong>配送方式：</strong> {order.shippingMethod.name}
                </p>
              )}
              {order.shippingMethod?.deliveryTime && (
                <p>
                  <strong>配達時段：</strong>{' '}
                  {DELIVERY_TIME_SLOTS[order.shippingMethod.deliveryTime] || order.shippingMethod.deliveryTime}
                </p>
              )}
              {(order.shippingAddress.mobile || order.shippingAddress.email) && (
                <p>
                  <strong>收件聯絡：</strong>{' '}
                  {[order.shippingAddress.mobile, order.shippingAddress.email].filter(Boolean).join(' · ')}
                </p>
              )}
              <p>
                <strong>地址：</strong> {order.shippingAddress.address}, {order.shippingAddress.city}{""}, {order.shippingAddress.postalCode}, {""}{order.shippingAddress.country} 
              </p>
              {order.isDelivered ? (
                <Message variant="success">
                  已於 {order.deliveredAt} 出貨
                </Message>
              ) : (
                <Message variant="danger">尚未出貨</Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>付款方式</h2>
              <p>
                <strong>方式：</strong>
                {order.paymentMethod}
              </p>
              {order.isPaid ? (
                <Message variant="success">
                  已於 {order.paidAt} 付款
                </Message>
              ) : (
                <Message variant="danger">尚未付款</Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>訂購商品</h2>
              {order.orderItems.map((item, index) => (
                <ListGroup.Item key={index}>
                  <Row>
                    <Col md={1}>
                      <Image src={item.image} alt={item.name} fluid rounded />
                    </Col>
                    <Col>
                      <Link to={`/product/${item.product}`}>
                        {item.name}
                      </Link>
                    </Col>
                    <Col>
                      {item.qty} x {formatNTD(item.price)} = {formatNTD(item.qty * item.price)}
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup.Item>
          </ListGroup>
        </Col>
        
        <Col md={4}>
          <Card>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2>訂單摘要</h2>
              </ListGroup.Item >

              <ListGroup.Item>       
                <Row>
                  <Col>商品小計</Col>
                  <Col>{formatNTD(order.itemsPrice)}</Col>
                </Row>
                <Row>
                  <Col>運費</Col>
                  <Col>{formatNTD(order.shippingPrice)}</Col>
                </Row>
                <Row>
                  <Col>稅金</Col>
                  <Col>{formatNTD(order.taxPrice)}</Col>
                </Row>
                <Row>
                  <Col>總計</Col>
                  <Col>{formatNTD(order.totalPrice)}</Col>
                </Row>
              </ListGroup.Item>
             
              { !order.isPaid && (
                <ListGroup.Item>
                  {loadingPay && <Loader />}

                  {isLinePay ? (
                    <LinePayButton orderId={orderId} />
                  ) : isPending ? (<Loader />) : (
                    <div>
                      {/* <Button
                      onClick={onApproveTest} 
                      style={{marginBottom: "10px"}}
                      >
                      Test Pay Order
                      </Button> */}
                      <div>
                        <PayPalButtons
                          createOrder={createOrder}
                          onApprove={onApprove}
                          onError={onError}
                        ></PayPalButtons>
                      </div>
                    </div>
                  )}
                </ListGroup.Item>
              )}
              { loadingDeliver && <Loader /> }

              { userInfo && userInfo.isAdmin && order.isPaid && !order.isDelivered && (
                <ListGroup.Item>
                  <Button
                    type="button"
                    className="btn btn-block"
                    onClick={deliverOrderHandler}
                  >
                    標記為已出貨
                  </Button>
                </ListGroup.Item>
              ) }
            </ListGroup>
          </Card>
        </Col>
      </Row>
    </>
  ) ;
}

export default OrderScreen